import { z } from "zod";

import { baseEntityFields } from "./base.js";

/**
 * Membre : la FICHE d'un utilisateur dans une org, et l'annuaire de ses DROITS.
 *
 * LE MODELE, en trois etages :
 *   1. le ROLE d'org (`orgRole`) : `owner`, `admin` (tout, plus la gestion des
 *      membres), `member` (ecrit partout par defaut), `viewer` (lit partout par
 *      defaut) ;
 *   2. les DEROGATIONS par PORTE (`portes`), une porte etant une famille du SDK
 *      plus le `type` du document. Elles sont rangees par famille puis par
 *      type, la clef `*` valant pour la famille entiere et pour les documents
 *      sans `type`, et ne s'ecrivent que pour DEROGER au defaut du role ;
 *   3. l'ACCES PAR APP (`apps`), une commodite de l'ecran des utilisateurs,
 *      traduite en portes a l'enregistrement : les rules ne lisent QUE `portes`.
 * Un membre `disabled` n'a plus rien, quel que soit son role.
 *
 * LA REGLE QUI FAIT FOI EST DANS LES RULES : `rangSur`, `firebase/firestore.rules`
 * (son en-tete porte l'ordre des clefs et ce que `*` a de particulier). Le meme
 * calcul est ecrit en JS pour griser l'ecran (`niveauEffectif`,
 * packages/ui/src/lib/acces.tsx) ; s'ils divergent, ce sont les rules qui ont
 * raison.
 *
 * L'IDENTIFIANT du document est l'EMAIL EN MINUSCULES (`emailEnId`,
 * packages/sdk/src/members.ts), et pas un id genere : les rules doivent
 * trouver la fiche de l'appelant en un seul `get()`, sans requete, donc sans
 * rien avoir a chercher.
 */

/**
 * Roles d'org (RBAC niveau org, claims minimaux cote Auth).
 * Les roles fins par app vivent dans Firestore (`/appMemberships`), pas ici.
 */
export const orgRoleSchema = z.enum(["owner", "admin", "member", "viewer"]);

/** Type d'un role d'org. */
export type OrgRole = z.infer<typeof orgRoleSchema>;

/** Les trois niveaux d'acces, du plus ferme au plus ouvert. */
export const niveauAccesSchema = z.enum(["none", "read", "write"]);

/** Type d'un niveau d'acces. */
export type NiveauAcces = z.infer<typeof niveauAccesSchema>;

/**
 * Les derogations d'un membre : famille -> (type, ou `*`) -> niveau.
 * Exemple : `{ assets: { materiel: 'read' }, contacts: { '*': 'none' } }`.
 * Une famille absente n'est pas une famille fermee : c'est une famille au
 * defaut du role.
 */
export const derogationsSchema = z.record(
  z.string(),
  z.record(z.string(), niveauAccesSchema),
);

/** Type du sac de derogations. */
export type Derogations = z.infer<typeof derogationsSchema>;

/**
 * Fiche membre : rattachement d'un utilisateur a une org, avec son role et ses
 * derogations. Etend les champs standard.
 *
 * - `orgId`       : org de rattachement.
 * - `email`       : email du membre, en MINUSCULES et EGAL a l'id du document.
 * - `userId`      : uid Firebase Auth, OPTIONNEL : une fiche s'ecrit avant que
 *                   la personne se soit connectee une premiere fois, et c'est
 *                   l'email qui la designe, pas l'uid.
 * - `personId`    : la PERSONNE, identifiant stable tire au sort a la creation,
 *                   jamais affiche, jamais modifie (`personne.ts`). C'est lui,
 *                   et non l'email, qu'une fiche d'une autre famille porte
 *                   pour designer quelqu'un. OPTIONNEL au schema pour les
 *                   fiches historiques, que le backfill complete ; le SDK et
 *                   les rules l'exigent a la creation.
 * - `displayName` : nom affiche, optionnel.
 * - `orgRole`     : role au niveau de l'org.
 * - `disabled`    : membre desactive (acces suspendu).
 * - `apps`        : niveau par app, la commodite de l'ecran (pas lue en base).
 * - `portes`      : les derogations, LUES PAR LES RULES.
 */
export const MemberSchema = z.object({
  ...baseEntityFields,
  orgId: z.string(),
  email: z.string().email(),
  personId: z.string().min(1).optional(),
  userId: z.string().optional(),
  displayName: z.string().optional(),
  orgRole: orgRoleSchema,
  disabled: z.boolean().default(false),
  apps: z.record(z.string(), niveauAccesSchema).default({}),
  portes: derogationsSchema.default({}),
});

/** Type d'un membre. */
export type Member = z.infer<typeof MemberSchema>;

/**
 * Schema des donnees fournies par l'appelant a la creation.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt,
 * schemaVersion) sont remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const MemberCreateInputSchema = z.object({
  orgId: z.string(),
  // `.trim()` AVANT `.email()` : ce qu'un humain tape dans un champ porte
  // souvent un espace, et un « Invalid email » sur une adresse correcte est le
  // genre de refus qu'on ne comprend pas. Le SDK retire de toute facon les
  // espaces pour construire l'identifiant (`emailEnId`) : les deux etages
  // doivent tolerer la meme chose, sinon la creation echoue la ou la lecture
  // marche.
  email: z.string().trim().email(),
  orgRole: orgRoleSchema,
  // Fourni seulement pour REPRENDRE une personne existante sous une autre
  // adresse (elle garde ses heures et ses fiches) ; absent, le SDK en tire un.
  personId: z.string().min(1).optional(),
  userId: z.string().optional(),
  displayName: z.string().optional(),
  disabled: z.boolean().optional(),
  apps: z.record(z.string(), niveauAccesSchema).optional(),
  portes: derogationsSchema.optional(),
});

/** Type de l'entree de creation d'un membre. */
export type MemberCreateInput = z.infer<typeof MemberCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `email` n'y sont PAS : le premier est immuable comme partout, le
 * second est l'IDENTIFIANT du document. Changer l'email, c'est faire une autre
 * fiche, et les Security Rules refusent de toute facon les deux. `personId`
 * n'y est pas non plus : il est immuable, une personne ne change pas d'identite.
 */
export const MemberUpdateInputSchema = z
  .object({
    userId: z.string(),
    displayName: z.string(),
    orgRole: orgRoleSchema,
    disabled: z.boolean(),
    apps: z.record(z.string(), niveauAccesSchema),
    portes: derogationsSchema,
  })
  .partial();

/** Type du patch de mise a jour d'un membre. */
export type MemberUpdateInput = z.infer<typeof MemberUpdateInputSchema>;
